import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CachedUser {
  id: string;
  name: string;
  role: string;
  banned: boolean;
  branchId?: string;
  customerCategory?: string;
  companyId?: string | null;
  created_at?: string;
  routeActive?: boolean;
  routeEnded?: boolean;
  isPartner?: boolean;
  payType?: string;
  payValue?: number;
  cityPrices?: any;
  phone?: string;
  address?: string;
}

interface UsersCacheValue {
  users: CachedUser[];
  drivers: CachedUser[];
  customers: CachedUser[];
  employees: CachedUser[];
  loading: boolean;
  reload: () => Promise<void>;
}

const UsersCacheContext = createContext<UsersCacheValue | null>(null);

export const UsersCacheProvider = ({ children }: { children: ReactNode }) => {
  const { user, role } = useAuth();
  const [users, setUsers] = useState<CachedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const inflightRef = useRef<Promise<void> | null>(null);
  const lastLoadedForRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    // Only staff can list users
    if (!user || !role || !['admin', 'employee', 'super_admin'].includes(role)) {
      setUsers([]);
      setLoading(false);
      return;
    }

    // Coalesce concurrent callers
    if (inflightRef.current) return inflightRef.current;

    const promise = (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('manage-users', {
          body: { action: 'list' },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        let list: CachedUser[] = data || [];
        const driverIds = list.filter(u => u.role === 'driver').map(u => u.id);
        if (driverIds.length > 0) {
          const { data: details } = await supabase
            .from('driver_details')
            .select('user_id, route_active, route_ended');
          if (details) {
            const map = new Map(details.map((d: any) => [d.user_id, d]));
            list = list.map(u => {
              if (u.role !== 'driver') return u;
              const det: any = map.get(u.id);
              return { ...u, routeActive: !!det?.route_active, routeEnded: !!det?.route_ended };
            });
          }
        }

        // Inject active delivery partners as synthetic drivers
        try {
          const { data: partners } = await supabase
            .from('delivery_partners' as any)
            .select('id, name, is_active, pay_type, pay_value, city_prices, phone, address')
            .eq('is_active', true);
          if (Array.isArray(partners) && partners.length > 0) {
            const partnerDrivers: CachedUser[] = (partners as any[]).map(p => ({
              id: p.id,
              name: p.name,
              role: 'driver',
              banned: false,
              isPartner: true,
              payType: p.pay_type || '',
              payValue: Number(p.pay_value) || 0,
              cityPrices: p.city_prices || [],
              phone: p.phone || '',
              address: p.address || '',
            }));
            list = [...list, ...partnerDrivers];
          }
        } catch { /* silent: partners optional */ }

        setUsers(list);
        lastLoadedForRef.current = user.id;
      } catch {
        // silent
      } finally {
        setLoading(false);
        inflightRef.current = null;
      }
    })();
    inflightRef.current = promise;
    return promise;
  }, [user, role]);

  useEffect(() => {
    if (user && role && lastLoadedForRef.current !== user.id) {
      setLoading(true);
      load();
    } else if (!user) {
      setUsers([]);
      setLoading(false);
      lastLoadedForRef.current = null;
    }
  }, [user, role, load]);

  const drivers = users.filter(u => u.role === 'driver');
  const customers = users.filter(u => u.role === 'customer');
  const employees = users.filter(u => u.role === 'employee' || u.role === 'admin');

  return (
    <UsersCacheContext.Provider value={{ users, drivers, customers, employees, loading, reload: load }}>
      {children}
    </UsersCacheContext.Provider>
  );
};

export const useUsersCache = () => {
  const ctx = useContext(UsersCacheContext);
  if (!ctx) throw new Error('useUsersCache must be used within UsersCacheProvider');
  return ctx;
};
